import { useState, useCallback } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';

/**
 * Pre-launch notice strip.
 *
 * The site is publicly reachable while still in development and is picking up
 * organic worldwide traffic, so visitors need to be told up front that this is
 * not a live shop and that nothing they do here is processed.
 *
 * AT LAUNCH: flip DEV_NOTICE_ENABLED to false and rebuild. That is the only
 * change needed — this file is the single switch, no env plumbing involved.
 *
 * The dismissal key is versioned on purpose: bump `_v1` to `_v2` and everyone
 * who already dismissed the old wording sees the strip again.
 */
const DEV_NOTICE_ENABLED = true;

const STORAGE_KEY = 'dialine_dev_notice_v1';

// localStorage throws in Safari private mode / when storage is disabled, and a
// notice that crashes the homepage is worse than no notice at all.
const readDismissed = () => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === 'dismissed';
  } catch {
    return false;
  }
};

export default function DevNotice() {
  const { t } = useLanguage();
  const [dismissed, setDismissed] = useState(readDismissed);

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(STORAGE_KEY, 'dismissed');
    } catch {
      // Non-persistent dismissal is an acceptable degradation.
    }
  }, []);

  if (!DEV_NOTICE_ENABLED || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      // Amber, deliberately not the brand red of the contact Banner above it —
      // it has to read as an alert, not as more site chrome.
      className="relative w-full bg-amber-50 border-b border-amber-300"
    >
      <div className="max-w-9xl mx-auto flex items-center gap-3 px-3 py-2 sm:px-6 lg:px-8">
        <svg
          className="h-4 w-4 flex-shrink-0 text-amber-600"
          fill="currentColor"
          viewBox="0 0 20 20"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
            clipRule="evenodd"
          />
        </svg>

        <p className="flex-1 text-xs sm:text-sm text-amber-900 leading-snug">
          <span className="font-semibold">{t('devNotice.title')}</span>{' '}
          <span className="text-amber-800">{t('devNotice.message')}</span>
        </p>

        <button
          type="button"
          onClick={handleDismiss}
          aria-label={t('devNotice.dismiss')}
          className="flex-shrink-0 p-1 rounded-md text-amber-700 hover:text-amber-900 hover:bg-amber-100 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 focus:ring-offset-amber-50"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
}
